import * as Sentry from '@sentry/react';
import type { AxiosError } from 'axios';
import { useCallback } from 'react';
import { toast } from 'sonner';
import { ProblemError } from '@/api/axios-client';

/**
 * API Error structure for consistent handling
 */
export interface ApiError {
  message: string;
  details?: string;
  code?: string | number;
  field?: string;
  retryAfter?: number;
}

/**
 * Parse different types of API errors into a consistent format
 * Works with both Problem format (RFC 9457) and legacy error formats
 */
export function parseApiError(error: unknown): ApiError {
  // Handle ProblemError (from our axios interceptor)
  if (error instanceof ProblemError) {
    return {
      message: error.problem.detail || error.problem.title,
      code: error.problem.status,
      retryAfter: error.retryAfter,
    };
  }

  // Handle axios errors
  if (error && typeof error === 'object' && 'response' in error) {
    const axiosError = error as AxiosError<any>;
    const response = axiosError.response;

    if (response?.data) {
      // Backend validation errors (V1 format)
      if (response.data.detail) {
        return {
          message: Array.isArray(response.data.detail)
            ? response.data.detail[0]?.msg || 'Validation error'
            : response.data.detail,
          details: Array.isArray(response.data.detail)
            ? response.data.detail.map((d: any) => d.msg).join(', ')
            : undefined,
          code: response.status,
        };
      }

      // Simple error message
      if (response.data.message) {
        return {
          message: response.data.message,
          code: response.status,
        };
      }
    }

    // HTTP status errors
    if (response?.status) {
      const statusMessages: Record<number, string> = {
        400: 'Bad request. Please check your input.',
        401: 'Authentication required. Please log in.',
        403: 'Access denied. You do not have permission.',
        404: 'Resource not found.',
        409: 'Conflict. The resource already exists.',
        422: 'Validation error. Please check your input.',
        429: 'Too many requests. Please try again later.',
        500: 'Server error. Please try again later.',
        502: 'Service temporarily unavailable.',
        503: 'Service temporarily unavailable.',
      };

      return {
        message: statusMessages[response.status] || `Error: ${response.status}`,
        code: response.status,
      };
    }

    // Network errors
    if (axiosError.code === 'ECONNABORTED') {
      return {
        message: 'Request timeout. Please try again.',
        code: 'TIMEOUT',
      };
    }

    if (axiosError.code === 'NETWORK_ERROR' || !axiosError.response) {
      return {
        message: 'Network error. Please check your connection.',
        code: 'NETWORK',
      };
    }
  }

  // Handle standard Error objects
  if (error instanceof Error) {
    return {
      message: error.message,
    };
  }

  // Handle string errors
  if (typeof error === 'string') {
    return {
      message: error,
    };
  }

  // Fallback
  return {
    message: 'An unexpected error occurred',
  };
}

/**
 * Hook for consistent API error handling with toast notifications
 *
 * Usage:
 * ```typescript
 * const { handleError, showError, showSuccess } = useApiErrorHandler();
 *
 * try {
 *   const result = await apiCall();
 *   showSuccess('Operation completed');
 * } catch (error) {
 *   handleError(error); // Automatically shows toast and reports to Sentry
 * }
 * ```
 */
export function useApiErrorHandler() {
  /**
   * Handle an API error with automatic toast notification and Sentry reporting
   */
  const handleError = useCallback(
    (
      error: unknown,
      options?: {
        silent?: boolean; // Don't show toast
        context?: string; // Additional context for logging
        fallbackMessage?: string; // Custom fallback message
      }
    ) => {
      const apiError = parseApiError(error);

      // Report to Sentry in production
      if (import.meta.env.PROD) {
        Sentry.captureException(error, {
          tags: {
            type: 'api_error',
            code: apiError.code?.toString(),
          },
          extra: {
            context: options?.context,
            details: apiError.details,
          },
        });
      }

      // Show toast unless silent
      if (!options?.silent) {
        toast.error(apiError.message, {
          description: apiError.details,
          duration: apiError.retryAfter ? apiError.retryAfter * 1000 : 5000,
        });
      }

      return apiError;
    },
    []
  );

  /**
   * Show an error toast manually
   */
  const showError = useCallback((message: string, details?: string) => {
    toast.error(message, {
      description: details,
      duration: 5000,
    });
  }, []);

  /**
   * Show a success toast
   */
  const showSuccess = useCallback((message: string, details?: string) => {
    toast.success(message, {
      description: details,
      duration: 4000,
    });
  }, []);

  /**
   * Show an info toast
   */
  const showInfo = useCallback((message: string, details?: string) => {
    toast.info(message, {
      description: details,
      duration: 4000,
    });
  }, []);

  /**
   * Show a loading toast that can be updated
   */
  const showLoading = useCallback((message: string) => {
    return toast.loading(message);
  }, []);

  /**
   * Update a toast (typically a loading toast)
   */
  const updateToast = useCallback(
    (id: string | number, message: string, type: 'success' | 'error' | 'info' = 'success') => {
      if (type === 'success') {
        toast.success(message, { id });
      } else if (type === 'error') {
        toast.error(message, { id });
      } else {
        toast.info(message, { id });
      }
    },
    []
  );

  return {
    handleError,
    showError,
    showSuccess,
    showInfo,
    showLoading,
    updateToast,
    parseApiError,
  };
}
